import { Alert, Heading, Skeleton, Table, Text } from '@ui/themed';
import { t } from 'i18next';
import { useHandlers } from '../../../context/page-context';
import { useVariantPricing } from '../../pricing';
import { I18N_COMPONENTS } from '../constants';

export function ProductPricingCard({ sku }: { sku: string | null }) {
  const { navigationHandler } = useHandlers();
  const { data: pricing, isLoading } = useVariantPricing(sku);

  const headings = [
    t(`${I18N_COMPONENTS}.pricing_card.headings.price_list`),
    t(`${I18N_COMPONENTS}.pricing_card.headings.quantity`),
    t(`${I18N_COMPONENTS}.pricing_card.headings.price`),
    t(`${I18N_COMPONENTS}.pricing_card.headings.unit_of_measure`),
  ];

  const rows = (pricing ?? []).flatMap((priceList) =>
    priceList.pricing.map((entry) => ({
      key: `${priceList.price_list_slug}-${entry.quantity}`,
      cells: [
        priceList.price_list_slug,
        String(entry.quantity),
        entry.price.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        entry.unit_of_measure ?? '-',
      ],
    })),
  );

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <Heading style="sm" level="h2">
          {t(`${I18N_COMPONENTS}.pricing_card.title`)}
        </Heading>
        {sku && (
          <button
            type="button"
            onClick={() => navigationHandler.navigate(`/pricing/editor/${sku}`, { newTab: true })}
            className="inline-flex items-center font-medium text-spark-purple hover:brightness-125"
          >
            {t(`${I18N_COMPONENTS}.pricing_card.manually_edit`)}
          </button>
        )}
      </div>

      {isLoading ? (
        <Skeleton />
      ) : !pricing || pricing.length === 0 ? (
        <Alert variant="warning" headingLevel="h3">
          {t(`${I18N_COMPONENTS}.pricing_card.no_price_lists`)}
        </Alert>
      ) : (
        <Table headings={headings} caption={t(`${I18N_COMPONENTS}.pricing_card.title`)}>
          {rows.map((row, index) => (
            <Table.Row key={row.key} id={row.key} index={index}>
              {row.cells.map((cell) => (
                <Table.Cell key={cell}>
                  <Text variant="secondary">{cell}</Text>
                </Table.Cell>
              ))}
            </Table.Row>
          ))}
        </Table>
      )}
    </section>
  );
}
